/**
 * <StarRating />
 *
 * Phase 17 — shared star UI.
 *
 * Two modes:
 *   * Display    — read-only, accepts `value` (1-5 or null), shows
 *                  filled / half / empty stars. Used on cards, hero,
 *                  review rows.
 *   * Interactive — accepts `onChange`, renders 5 click targets with
 *                  48px tap area (per Parts Buoy design system).
 *
 * No external icon library — drawing stars via SVG path keeps it
 * crisp at every resolution and avoids pulling another dep.
 */
import * as React from "react";
import { cn } from "../../lib/cn";

interface StarProps {
  fillPercent: number; // 0-100
  size: number;
}

function StarSvg({ fillPercent, size }: StarProps) {
  // Unique gradient id per render so multiple stars on the page don't
  // collide. React's useId is stable across renders, perfect here.
  const id = React.useId();
  const path =
    "M12 2l2.95 6.99 7.55.66-5.71 5.07L18.7 22 12 18.04 5.3 22l1.91-7.28L1.5 9.65l7.55-.66L12 2z";
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <defs>
        <linearGradient id={id} x1="0" x2="1" y1="0" y2="0">
          <stop offset={`${fillPercent}%`} stopColor="#F5B400" />
          <stop offset={`${fillPercent}%`} stopColor="#D1D5DB" />
        </linearGradient>
      </defs>
      <path d={path} fill={`url(#${id})`} stroke="#9CA3AF" strokeWidth="0.5" />
    </svg>
  );
}

export interface StarRatingProps {
  /** 1-5 (fractional allowed) or null/undefined for "no reviews yet". */
  value: number | null | undefined;
  /** Star pixel size. Defaults to 16 (card-sized). */
  size?: number;
  /** Optional review_count rendered after the stars as "(N)". */
  count?: number | null;
  className?: string;
  /** When set, switches to interactive mode (1-5 integer rating). */
  onChange?: (next: number) => void;
  /** Accessible label override. */
  ariaLabel?: string;
}

export function StarRating({
  value,
  size = 16,
  count,
  className,
  onChange,
  ariaLabel,
}: StarRatingProps) {
  const interactive = !!onChange;
  // For interactive mode we hover-preview before commit.
  const [hover, setHover] = React.useState<number | null>(null);
  const effective = hover ?? value ?? 0;

  const stars = [1, 2, 3, 4, 5].map((i) => {
    let pct = 0;
    if (effective >= i) pct = 100;
    else if (effective > i - 1) pct = Math.round((effective - (i - 1)) * 100);
    if (interactive) {
      return (
        <button
          key={i}
          type="button"
          aria-label={`Rate ${i} star${i === 1 ? "" : "s"}`}
          onMouseEnter={() => setHover(i)}
          onMouseLeave={() => setHover(null)}
          onFocus={() => setHover(i)}
          onBlur={() => setHover(null)}
          onClick={() => onChange?.(i)}
          // 48px tap target per Parts Buoy design system. Visual size
          // is `size`; the surrounding button stays full 48 to keep
          // touch ergonomics correct.
          className="inline-flex items-center justify-center w-12 h-12 hover:scale-110 transition-transform"
        >
          <StarSvg fillPercent={pct} size={size * 1.5} />
        </button>
      );
    }
    return <StarSvg key={i} fillPercent={pct} size={size} />;
  });

  // Show a muted "No reviews" if value is null AND we're not interactive.
  if (!interactive && (value === null || value === undefined)) {
    return (
      <span
        className={cn(
          "inline-flex items-center text-text-secondary text-sm",
          className,
        )}
        aria-label={ariaLabel ?? "No reviews yet"}
      >
        <span className="opacity-60 inline-flex gap-0.5 mr-1.5">
          {[1, 2, 3, 4, 5].map((i) => (
            <StarSvg key={i} fillPercent={0} size={size} />
          ))}
        </span>
        No reviews yet
      </span>
    );
  }

  const label =
    ariaLabel ??
    (interactive
      ? `Select a rating, currently ${effective || 0} of 5`
      : `Rated ${value?.toFixed?.(1) ?? value} of 5`);

  return (
    <span
      className={cn("inline-flex items-center", className)}
      role="img"
      aria-label={label}
    >
      <span className={cn("inline-flex", interactive ? "" : "gap-0.5")}>
        {stars}
      </span>
      {!interactive && value !== null && value !== undefined && (
        <span className="ml-1.5 text-sm font-medium text-text-primary">
          {value.toFixed(1)}
        </span>
      )}
      {!interactive && typeof count === "number" && count > 0 && (
        <span className="ml-1 text-sm text-text-secondary">({count})</span>
      )}
    </span>
  );
}
